import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  arrayUnion,
  limit,
  getDoc,
  orderBy,
  startAfter,
  getDocsFromCache,
  writeBatch
} from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';

// Sistema de caché para mejorar el rendimiento
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos en milisegundos

// Constantes para control de concurrencia
const MAX_CONCURRENT_REQUESTS = 3; // Máximo número de consultas simultáneas
const REQUEST_DELAY = 100; // Delay entre consultas en ms

// Control de consultas concurrentes
let activeRequests = 0;
const requestQueue = [];

/**
 * Función para controlar las consultas concurrentes
 */
const executeWithConcurrencyControl = async (requestFunction) => {
  return new Promise((resolve, reject) => {
    const executeRequest = async () => {
      if (activeRequests >= MAX_CONCURRENT_REQUESTS) {
        // Si hay demasiadas consultas activas, encolar
        requestQueue.push({ executeRequest, resolve, reject });
        return;
      }

      activeRequests++;
      
      try {
        // Añadir delay entre consultas
        if (activeRequests > 1) {
          await new Promise(resolve => setTimeout(resolve, REQUEST_DELAY));
        }

        const result = await requestFunction();
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        activeRequests--;
        
        // Procesar siguiente consulta en la cola
        if (requestQueue.length > 0) {
          const nextRequest = requestQueue.shift();
          nextRequest.executeRequest();
        }
      }
    };

    executeRequest();
  });
};

/**
 * Función para ejecutar consultas con retry y backoff exponencial
 */
const executeWithRetry = async (requestFunction, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await executeWithConcurrencyControl(requestFunction);
    } catch (error) {
      lastError = error;
      
      // Si es un error de "Too many outstanding requests", esperar más tiempo
      if (error.code === 'resource-exhausted' || error.message.includes('Too many outstanding requests')) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 10000); // Backoff exponencial, máximo 10s
        console.log(`Error de concurrencia, reintentando en ${delay}ms (intento ${attempt + 1}/${maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // Para otros errores, no reintentar
      throw error;
    }
  }
  
  throw lastError;
};

// Función para generar claves de caché
const generateCacheKey = (serviceId, operation, params = {}) => {
  const paramsString = Object.keys(params).length > 0 
    ? JSON.stringify(params) 
    : '';
  return `${serviceId}_${operation}_${paramsString}`;
};

// Función para verificar si el caché es válido
const isCacheValid = (cacheEntry) => {
  if (!cacheEntry) return false;
  const now = Date.now();
  return (now - cacheEntry.timestamp) < CACHE_DURATION;
};

// Función para obtener datos del caché
const getFromCache = (key) => {
  const cacheEntry = cache.get(key);
  if (isCacheValid(cacheEntry)) {
    console.log(`Cache hit: ${key}`);
    return cacheEntry.data;
  }
  if (cacheEntry) {
    console.log(`Cache expired: ${key}`);
    cache.delete(key);
  }
  return null;
};

// Función para guardar datos en caché
const setCache = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
  console.log(`Cache set: ${key}`);
};

// Dedup de peticiones idénticas en vuelo: si dos callers piden la misma clave
// antes de que la primera resuelva (típico en la carga en frío del dashboard,
// donde varios widgets resuelven el mismo scope a la vez), comparten la misma
// promesa en lugar de lanzar consultas duplicadas a Firestore.
const inflight = new Map();
const dedupInflight = (key, fn) => {
  if (inflight.has(key)) return inflight.get(key);
  const p = (async () => {
    try {
      return await fn();
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
};

// Función para limpiar caché específico
const clearCache = (serviceId = null) => {
  if (serviceId) {
    // Limpiar solo el caché del servicio específico
    for (const [key] of cache) {
      if (key.startsWith(`${serviceId}_`)) {
        cache.delete(key);
      }
    }
    console.log(`Cache cleared for service: ${serviceId}`);
  } else {
    // Limpiar todo el caché
    cache.clear();
    console.log('All cache cleared');
  }
};

// Función para obtener datos con caché
const getDataWithCache = async (key, fetchFunction) => {
  // Intentar obtener del caché primero
  const cachedData = getFromCache(key);
  if (cachedData) {
    return cachedData;
  }

  // Si no está en caché, obtener de Firestore
  try {
    const data = await fetchFunction();
    setCache(key, data);
    return data;
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
};


export const fetchAdmins = async (currentService) => {
  const adminQuery = query(collection(db, 'servicios', currentService.id, 'admins'));
  const adminSnapshot = await getDocs(adminQuery);
  return adminSnapshot.docs.map((doc) => {
    return { id: doc.id, ...doc.data() };
  });
};

/**
 * Devuelve TODOS los admins del servicio (cacheado 5 min). Pensado para
 * usuarios con scope (inspector/administrativo) en el menú de usuarios: la
 * colección admins es pequeña y el filtrado por delegación es client-side, así
 * que conviene traerla entera de una vez en lugar de paginar 50 en 50
 * escaneando la colección completa para encontrar los pocos pares visibles.
 */
export const fetchAdminsCompleto = async (currentService) => {
  if (!currentService?.id) return [];
  const cacheKey = `admins_completo_${currentService.id}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const adminSnapshot = await getDocs(
    collection(db, 'servicios', currentService.id, 'admins')
  );
  const admins = adminSnapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      nombre: data.nombre || data.nombreCliente || `Usuario ${doc.id}`,
      apellido: data.apellido || '',
      correo: data.correo || '',
      tipoUsuario: data.tipoUsuario || [],
      delegacionesAsignadas: data.delegacionesAsignadas || [],
      eliminado: data.eliminado || false,
      ...data,
    };
  });
  setCache(cacheKey, admins);
  return admins;
};

// Función paginada para obtener admins con caché
export const fetchAdminsPaginados = async (currentService, pageSize = 50, lastDoc = null, filtros = {}) => {
  try {
    // Generar clave de caché
    const cacheKey = generateCacheKey(currentService.id, 'admins_paginados', {
      pageSize,
      lastDocId: lastDoc?.id || null,
      filtros
    });

    // Intentar obtener del caché primero (solo para la primera página)
    if (!lastDoc) {
      const cachedData = getFromCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }
    }

    let q = query(
      collection(db, 'servicios', currentService.id, 'admins'),
      orderBy('nombre', 'asc'),
      limit(pageSize)
    );

    // Aplicar filtros si existen
    if (filtros.eliminado !== undefined) {
      q = query(
        collection(db, 'servicios', currentService.id, 'admins'),
        where('eliminado', '==', filtros.eliminado),
        orderBy('nombre', 'asc'),
        limit(pageSize)
      );
    }

    // Aplicar startAfter si hay un documento anterior
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }

    const snapshot = await getDocs(q);
    const admins = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        nombre: data.nombre || data.nombreCliente || `Usuario ${doc.id}`,
        apellido: data.apellido || '',
        correo: data.correo || '',
        tipoUsuario: data.tipoUsuario || [],
        clientes: data.clientes || [],
        tiendas: data.tiendas || [],
        eliminado: data.eliminado || false,
        ...data
      };
    });

    const hasMore = snapshot.docs.length === pageSize;
    const lastVisible = snapshot.docs[snapshot.docs.length - 1];

    const result = {
      admins,
      hasMore,
      lastDoc: lastVisible
    };

    // Guardar en caché solo la primera página
    if (!lastDoc) {
      setCache(cacheKey, result);
    }

    return result;
  } catch (error) {
    console.error('Error fetching admins:', error);
    throw error;
  }
};


export const fetchClientesYTiendas = async (currentService) => {
  const serviceId = currentService.id;
  const cacheKey = generateCacheKey(serviceId, 'clientes_tiendas_v2');
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  const clientesQuery = query(
    collection(db, 'servicios', serviceId, 'clientes'),
    where('borrado', '==', false)
  );
  const tiendasQuery = query(
    collection(db, 'servicios', serviceId, 'tiendas'),
    where('borrado', '==', false)
  );

  const [clientesSnapshot, tiendasSnapshot] = await Promise.all([
    getDocs(clientesQuery),
    getDocs(tiendasQuery)
  ]);

  const clientesData = clientesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  const tiendasData = tiendasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

  const result = [clientesData, tiendasData];
  setCache(cacheKey, result);
  return result;
};


export const addNewUser = async (newUserData, currentService) => {
  const { user } = await createUserWithEmailAndPassword(auth, newUserData.correo, newUserData.password);
  const usuarioDoc = doc(db, 'servicios', currentService.id, 'admins');
  await addDoc(usuarioDoc, {
    correo: newUserData.correo,
    nombre: newUserData.nombre,
    tipoUsuario: newUserData.tipoUsuario,
    userId: user.uid
  });
  return user.uid; // Devuelve el ID del nuevo usuario
};


export const updateClientesYTiendas = async (userId, tipoUsuario, newUserData, currentService) => {
  const esAdministrativo = tipoUsuario.includes('administrativo');
  const esInspector = tipoUsuario.includes('inspector');
  const batch = writeBatch(db);

  for (const clienteId of newUserData.clientes) {
    const ref = doc(db, `servicios/${currentService.id}/clientes/${clienteId}`);
    if (esAdministrativo) {
      batch.update(ref, { administrativos: arrayUnion(userId) });
    }
    if (esInspector) {
      batch.update(ref, { inspectores: arrayUnion(userId) });
    }
  }

  for (const tiendaId of newUserData.tiendas) {
    const ref = doc(db, `servicios/${currentService.id}/tiendas/${tiendaId}`);
    if (esAdministrativo) {
      batch.update(ref, { administrativos: arrayUnion(userId) });
    }
    if (esInspector) {
      batch.update(ref, { inspectores: arrayUnion(userId) });
    }
  }

  await batch.commit();
};

export const fetchUserData = async (currentUser, currentService) => {
  if (currentUser && currentService) {
    try {
      // Generar clave de caché
      const cacheKey = generateCacheKey(currentService.id, 'user_data', {
        userEmail: currentUser.email
      });

      // Intentar obtener del caché primero
      const cachedData = getFromCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const adminQuery = query(
        collection(db, 'servicios', currentService.id, 'admins'),
        where('correo', '==', currentUser.email),
        limit(1)
      );
      const adminSnapshot = await getDocs(adminQuery);

      if (!adminSnapshot.empty) {
        const adminData = adminSnapshot.docs[0].data();
        const adminId = adminSnapshot.docs[0].id;
        const result = { ...adminData, id: adminId };
        
        // Guardar en caché
        setCache(cacheKey, result);
        
        return result;
      } else {
        throw new Error("No se encontraron datos del administrador.");
      }
    } catch (error) {
      throw error;
    }
  }
};

// Función para obtener clientes por sus IDs (usa adminData.clientes como fuente de verdad)
// Necesaria porque updateClientesYTiendas está desactivada y los arrays administrativos/inspectores
// en los documentos de cliente pueden estar desincronizados
export const fetchClientesPorIds = async (clientesIds, servicio, adminId = null) => {
  if (!servicio?.id || !clientesIds?.length) return [];

  const cacheKey = `clientes_por_ids_${servicio.id}_${adminId || 'anon'}_${clientesIds.length}`;
  const cachedData = getFromCache(cacheKey);
  if (cachedData) return cachedData;

  const clientesRef = collection(db, 'servicios', servicio.id, 'clientes');
  const BATCH_SIZE = 50;
  const clientes = [];

  for (let i = 0; i < clientesIds.length; i += BATCH_SIZE) {
    const batch = clientesIds.slice(i, i + BATCH_SIZE);
    const promises = batch.map((clienteId) => getDoc(doc(clientesRef, clienteId)));
    const docs = await Promise.all(promises);

    docs.forEach((docSnap) => {
      if (docSnap.exists() && !docSnap.data().borrado) {
        clientes.push({ id: docSnap.id, ...docSnap.data() });
      }
    });
  }

  setCache(cacheKey, clientes);
  return clientes;
};

// Función para obtener clientes basada en el ID del administrador con caché
export const fetchClientesPorAdministrativo = async (adminId, servicio) => {
  console.log('fetchClientesPorAdministrativo llamado con:', { adminId, servicioId: servicio?.id });
  
  // Generar clave de caché
  const cacheKey = `clientes_administrativo_${servicio.id}_${adminId}`;
  
  // Intentar obtener del caché primero
  const cachedData = getFromCache(cacheKey);
  if (cachedData) {
    console.log('Usando datos en caché para administrativo');
    return cachedData;
  }
  
  const clientesRef = collection(db, 'servicios', servicio.id, 'clientes');
  const q = query(clientesRef, where('administrativos', 'array-contains', adminId));
  const querySnapshot = await getDocs(q);
  
  console.log(`Encontrados ${querySnapshot.size} clientes para administrativo ${adminId}`);
  
  const clientes = [];
  querySnapshot.forEach((doc) => {
    const clienteData = { id: doc.id, ...doc.data() };
    console.log('Cliente encontrado:', clienteData);
    clientes.push(clienteData);
  });
  
  console.log('Clientes finales:', clientes);
  
  // Guardar en caché por 5 minutos
  setCache(cacheKey, clientes);
  
  return clientes;
};

// Función para obtener clientes basada en el ID del inspector con caché
/**
 * Devuelve las TIENDAS asignadas a un inspector según su `INSP_COD`
 * Freematica (campo `inspectorCod` en cada tienda). Solo tiendas
 * con `activoFreematica:true` y `borrado != true`.
 * @param {string} inspectorCodFM - código Freematica del inspector.
 * @param {Object} servicio - servicio actual.
 * @return {Promise<Array>} tiendas.
 */
export const fetchTiendasPorInspector = async (inspectorCodFM, servicio) => {
  if (!inspectorCodFM || !servicio?.id) return [];
  const cacheKey = `tiendas_inspector_${servicio.id}_${inspectorCodFM}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  return dedupInflight(cacheKey, async () => {
    const c2 = getFromCache(cacheKey);
    if (c2) return c2;

    const tiendasRef = collection(db, 'servicios', servicio.id, 'tiendas');
    const q = query(
      tiendasRef,
      where('inspectorCod', '==', inspectorCodFM),
      where('activoFreematica', '==', true)
    );
    const snap = await getDocs(q);
    const tiendas = [];
    snap.forEach((doc) => {
      const d = doc.data();
      if (d.borrado !== true) tiendas.push({ id: doc.id, ...d });
    });
    setCache(cacheKey, tiendas);
    return tiendas;
  });
};

/**
 * Devuelve los CLIENTES asignados a un inspector. Se derivan a partir
 * de sus tiendas activas: se carga el set de tiendas (vía
 * fetchTiendasPorInspector), se recogen los `cliente` docIds únicos
 * y se cargan esos clientes con `where(documentId(), in, chunk)`.
 *
 * Compatibilidad: si `inspectorCodFM` es null o vacío, se cae al
 * modelo legacy (array `inspectores`).
 *
 * @param {string} inspectorCodFM - código Freematica.
 * @param {Object} servicio - servicio actual.
 * @param {string} [adminIdLegacy] - uid Sentry para fallback legacy.
 * @return {Promise<Array>} clientes.
 */
export const fetchClientesPorInspector = async (inspectorCodFM, servicio, adminIdLegacy = null) => {
  if (!servicio?.id) return [];

  // Fallback legacy si no hay inspectorCodFM
  if (!inspectorCodFM) {
    if (!adminIdLegacy) return [];
    const cacheKey = `clientes_inspector_legacy_${servicio.id}_${adminIdLegacy}`;
    const cached = getFromCache(cacheKey);
    if (cached) return cached;
    const clientesRef = collection(db, 'servicios', servicio.id, 'clientes');
    const q = query(clientesRef, where('inspectores', 'array-contains', adminIdLegacy));
    const snap = await getDocs(q);
    const clientes = [];
    snap.forEach((d) => clientes.push({ id: d.id, ...d.data() }));
    setCache(cacheKey, clientes);
    return clientes;
  }

  const cacheKey = `clientes_inspector_${servicio.id}_${inspectorCodFM}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  return dedupInflight(cacheKey, async () => {
    const c2 = getFromCache(cacheKey);
    if (c2) return c2;

    // 1) Tiendas del inspector
    const tiendas = await fetchTiendasPorInspector(inspectorCodFM, servicio);
    // 2) Set de cliente docIds únicos
    const clienteIds = [...new Set(
      tiendas.map((t) => t.cliente).filter((c) => c && typeof c === 'string')
    )];
    if (clienteIds.length === 0) {
      setCache(cacheKey, []);
      return [];
    }

    // 3) Cargar clientes en chunks de 10 (límite Firestore `in`), en paralelo
    const clientesRef = collection(db, 'servicios', servicio.id, 'clientes');
    const chunks = [];
    for (let i = 0; i < clienteIds.length; i += 10) chunks.push(clienteIds.slice(i, i + 10));
    const snaps = await Promise.all(
      chunks.map((chunk) => getDocs(query(clientesRef, where('__name__', 'in', chunk))))
    );
    const clientes = [];
    snaps.forEach((snap) => snap.forEach((d) => {
      const data = d.data();
      if (data.borrado !== true) clientes.push({ id: d.id, ...data });
    }));
    setCache(cacheKey, clientes);
    return clientes;
  });
};

/**
 * Devuelve las TIENDAS cuyo campo `delegacion` está incluido en
 * `delegaciones` (códigos Freematica). Solo tiendas con
 * `activoFreematica !== false` y `borrado !== true`.
 *
 * Si se pasa `empresas` (array de `codEmpresa`), se exige AND con
 * `codEmpresa ∈ empresas` (array vacío/omitido = sin restricción por empresa).
 * Firestore solo admite un `in` por consulta —ya en uso por `delegacion`—, así
 * que el recorte por empresa se hace en memoria sobre el resultado.
 *
 * Pagina internamente en chunks de 10 (límite Firestore `in`).
 */
export const fetchTiendasPorDelegacion = async (delegaciones, servicio, empresas = []) => {
  if (!servicio?.id) return [];
  const delegs = Array.isArray(delegaciones)
    ? delegaciones.map((d) => (d == null ? '' : String(d).trim())).filter(Boolean)
    : [];
  if (delegs.length === 0) return [];

  const emps = Array.isArray(empresas)
    ? empresas.map((e) => (e == null ? '' : String(e).trim())).filter(Boolean)
    : [];
  const empresasSet = emps.length > 0 ? new Set(emps) : null;

  // La clave de caché incluye las empresas: distinto filtro de empresa =
  // resultado distinto (no reutilizar la lista sin recortar por empresa).
  const empKey = emps.length > 0 ? `__emp_${[...emps].sort().join(',')}` : '';
  const cacheKey = `tiendas_delegacion_${servicio.id}_${[...delegs].sort().join(',')}${empKey}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  return dedupInflight(cacheKey, async () => {
    const c2 = getFromCache(cacheKey);
    if (c2) return c2;

    const tiendasRef = collection(db, 'servicios', servicio.id, 'tiendas');
    const seen = new Set();
    const tiendas = [];

    const chunks = [];
    for (let i = 0; i < delegs.length; i += 10) chunks.push(delegs.slice(i, i + 10));
    const snaps = await Promise.all(
      chunks.map((chunk) => getDocs(query(tiendasRef, where('delegacion', 'in', chunk))))
    );
    snaps.forEach((snap) => snap.forEach((d) => {
      if (seen.has(d.id)) return;
      const data = d.data();
      if (data.borrado === true) return;
      if (data.activoFreematica === false) return;
      if (empresasSet) {
        const emp = data.codEmpresa ? String(data.codEmpresa).trim() : '';
        if (!(emp && empresasSet.has(emp))) return;
      }
      seen.add(d.id);
      tiendas.push({ id: d.id, ...data });
    }));

    setCache(cacheKey, tiendas);
    return tiendas;
  });
};

/**
 * Devuelve los CLIENTES derivados de las tiendas con delegacion ∈ delegaciones.
 * Se cargan vía documentId `in` en chunks de 10.
 *
 * Si se pasa `empresas` (array de `codEmpresa`), los clientes se derivan SOLO de
 * las tiendas que además pertenezcan a esas empresas (AND) — así un cliente cuyas
 * tiendas en la delegación sean todas de otra empresa no aparece.
 */
export const fetchClientesPorDelegacion = async (delegaciones, servicio, empresas = []) => {
  if (!servicio?.id) return [];
  const delegs = Array.isArray(delegaciones)
    ? delegaciones.map((d) => (d == null ? '' : String(d).trim())).filter(Boolean)
    : [];
  if (delegs.length === 0) return [];

  const emps = Array.isArray(empresas)
    ? empresas.map((e) => (e == null ? '' : String(e).trim())).filter(Boolean)
    : [];
  const empKey = emps.length > 0 ? `__emp_${[...emps].sort().join(',')}` : '';
  const cacheKey = `clientes_delegacion_${servicio.id}_${[...delegs].sort().join(',')}${empKey}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  return dedupInflight(cacheKey, async () => {
    const c2 = getFromCache(cacheKey);
    if (c2) return c2;

    const tiendas = await fetchTiendasPorDelegacion(delegs, servicio, emps);
    const clienteIds = [...new Set(
      tiendas.map((t) => t.cliente).filter((c) => c && typeof c === 'string')
    )];
    if (clienteIds.length === 0) {
      setCache(cacheKey, []);
      return [];
    }

    const clientesRef = collection(db, 'servicios', servicio.id, 'clientes');
    const chunks = [];
    for (let i = 0; i < clienteIds.length; i += 10) chunks.push(clienteIds.slice(i, i + 10));
    const snaps = await Promise.all(
      chunks.map((chunk) => getDocs(query(clientesRef, where('__name__', 'in', chunk))))
    );
    const clientes = [];
    snaps.forEach((snap) => snap.forEach((d) => {
      const data = d.data();
      if (data.borrado !== true) clientes.push({ id: d.id, ...data });
    }));
    setCache(cacheKey, clientes);
    return clientes;
  });
};

/**
 * Devuelve los VIGILANTES cuyo campo `delegacion` está incluido en `delegaciones`.
 * Filtra `borrado !== true` en cliente.
 */
export const fetchVigilantesPorDelegacion = async (delegaciones, servicio) => {
  if (!servicio?.id) return [];
  const delegs = Array.isArray(delegaciones)
    ? delegaciones.map((d) => (d == null ? '' : String(d).trim())).filter(Boolean)
    : [];
  if (delegs.length === 0) return [];

  const cacheKey = `vigilantes_delegacion_${servicio.id}_${[...delegs].sort().join(',')}`;
  const cached = getFromCache(cacheKey);
  if (cached) return cached;

  return dedupInflight(cacheKey, async () => {
    const c2 = getFromCache(cacheKey);
    if (c2) return c2;

    const vigilantesRef = collection(db, 'servicios', servicio.id, 'vigilantes');
    const seen = new Set();
    const vigilantes = [];

    const chunks = [];
    for (let i = 0; i < delegs.length; i += 10) chunks.push(delegs.slice(i, i + 10));
    const snaps = await Promise.all(
      chunks.map((chunk) => getDocs(query(vigilantesRef, where('delegacion', 'in', chunk))))
    );
    snaps.forEach((snap) => snap.forEach((d) => {
      if (seen.has(d.id)) return;
      const data = d.data();
      if (data.borrado === true) return;
      seen.add(d.id);
      vigilantes.push({ id: d.id, ...data });
    }));

    setCache(cacheKey, vigilantes);
    return vigilantes;
  });
};

export const fetchTiendasPorCampoCliente = async (clientesIds, servicio, opts = {}) => {
  if (!servicio?.id) {
    console.error('No hay servicio válido proporcionado');
    return [];
  }

  // Si soloActivosFreematica:true, excluimos tiendas con
  // activoFreematica:false (las legacy inactivas tras desactivación).
  // Las tiendas sin campo activoFreematica (manuales puras) se conservan.
  const soloActivos = opts.soloActivosFreematica !== false; // default true

  const tiendasRef = collection(db, 'servicios', servicio.id, 'tiendas');
  let tiendas = [];

  // Consultar tiendas en batches usando operador 'in' (max 30 por query)
  const seenIds = new Set();
  for (let i = 0; i < clientesIds.length; i += 30) {
    const chunk = clientesIds.slice(i, i + 30);
    const q = query(tiendasRef, where('cliente', 'in', chunk));
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
      if (!seenIds.has(doc.id)) {
        seenIds.add(doc.id);
        tiendas.push({ id: doc.id, ...doc.data() });
      }
    });
  }

  const filtradas = tiendas.filter((t) => {
    if (t.borrado === true) return false;
    if (soloActivos && t.activoFreematica === false) return false;
    return true;
  });
  return filtradas;
};


export const fetchAdminData = async (currentUser, currentService) => {
  if (currentUser && currentService) {
    try {
      const adminQuery = query(
        collection(db, 'servicios', currentService.id, 'admins'),
        where('correo', '==', currentUser.email),
        limit(1)
      );
      const adminSnapshot = await getDocs(adminQuery);

      if (!adminSnapshot.empty) {
        const adminData = adminSnapshot.docs[0].data();
        return { ...adminData, id: adminSnapshot.docs[0].id };
      } else {
        throw new Error("No se encontraron datos del administrador.");
      }
    } catch (error) {
      throw error;
    }
  }
};

export const fetchAOtherdminData = async (adminId, currentService) => {
  if (adminId && currentService) {
    try {
      // Generar clave de caché
      const cacheKey = generateCacheKey(currentService.id, 'admin_data', {
        adminId
      });

      // Intentar obtener del caché primero
      const cachedData = getFromCache(cacheKey);
      if (cachedData) {
        return cachedData;
      }

      const adminRef = doc(db, 'servicios', currentService.id, 'admins', adminId);
      const adminSnapshot = await getDoc(adminRef);

      if (adminSnapshot.exists()) {
        const adminData = adminSnapshot.data();
        const result = { ...adminData, id: adminSnapshot.id };
        
        // Guardar en caché
        setCache(cacheKey, result);
        
        return result;
      } else {
        throw new Error("No se encontraron datos del administrador.");
      }
    } catch (error) {
      throw error;
    }
  }
};

// Función optimizada para cargar clientes y tiendas con caché
export const fetchClientesYTiendasConCache = async (currentService) => {
  try {
    // Generar clave de caché
    const cacheKey = generateCacheKey(currentService.id, 'clientes_tiendas');

    // Intentar obtener del caché primero
    const cachedData = getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    const serviceId = currentService.id;
    const clientesQuery = query(
      collection(db, 'servicios', serviceId, 'clientes'),
      where('borrado', '==', false)
    );
    const tiendasQuery = query(
      collection(db, 'servicios', serviceId, 'tiendas'),
      where('borrado', '==', false)
    );

    const [clientesSnapshot, tiendasSnapshot] = await Promise.all([
      getDocs(clientesQuery),
      getDocs(tiendasQuery)
    ]);

    const clientesData = clientesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const tiendasData = tiendasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const result = [clientesData, tiendasData];
    
    // Guardar en caché
    setCache(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error('Error fetching clientes y tiendas:', error);
    throw error;
  }
};

// Cache local + deduplicación de requests en curso para fetchVigilantesPorServicio.
// Antes: cada caller (GridView, AsignarVigilante, CreateTramo, FirestoreCuadrantes,
// 5+ sitios) disparaba un getDocs sin paginar de la colección entera (2.500+ docs
// en SECOEX) y ninguno cacheaba — varias pantallas hacían 3-4 fetches en cascada.
const _vigilantesCache = new Map(); // serviceId -> { data, expiresAt }
const _vigilantesInflight = new Map(); // serviceId -> Promise
const VIGILANTES_TTL_MS = 60_000;

// Función para obtener vigilantes filtrados por servicio asignado
export const fetchVigilantesPorServicio = async (servicio) => {
  if (!servicio?.id) return [];

  const serviceId = servicio.id;
  const cached = _vigilantesCache.get(serviceId);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.data;
  }
  const inflight = _vigilantesInflight.get(serviceId);
  if (inflight) return inflight;

  const promise = (async () => {
    try {
      // Mantener el fetch sin where('borrado','==',false) por compatibilidad:
      // hay vigilantes legacy sin el campo y un filtro server-side los excluiría.
      const vigilantesRef = collection(db, 'servicios', serviceId, 'vigilantes');
      const querySnapshot = await getDocs(vigilantesRef);

      const vigilantesList = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          name: data.name || data.nombre || data.email || 'Sin nombre',
          email: data.email || '',
          nombre: data.nombre || data.name || '',
          apellidos: data.apellidos || '',
          displayName: data.name ||
            (data.nombre && data.apellidos ? `${data.nombre} ${data.apellidos}` :
             data.nombre || data.email || 'Sin nombre')
        };
      });

      _vigilantesCache.set(serviceId, { data: vigilantesList, expiresAt: Date.now() + VIGILANTES_TTL_MS });
      return vigilantesList;
    } catch (error) {
      console.error('Error fetching vigilantes por servicio:', error);
      return [];
    } finally {
      _vigilantesInflight.delete(serviceId);
    }
  })();

  _vigilantesInflight.set(serviceId, promise);
  return promise;
};

// Invalida la caché — llamar tras crear/editar/borrar vigilantes.
export const invalidateVigilantesCache = (serviceId) => {
  if (serviceId) _vigilantesCache.delete(serviceId);
  else _vigilantesCache.clear();
};

// Nuevas funciones optimizadas para carga paginada
export const fetchClientesPaginados = async (currentService, pageSize = 50, lastDoc = null, filtros = {}) => {
  const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
  let q;
  
  // Construir la consulta base
  if (filtros.comunidad) {
    q = query(
      clientesRef, 
      where('comunidad', '==', filtros.comunidad),
      where('borrado', '==', false),
      orderBy('nombre'), 
      limit(pageSize)
    );
  } else if (filtros.provincia) {
    q = query(
      clientesRef, 
      where('provincia', '==', filtros.provincia),
      where('borrado', '==', false),
      orderBy('nombre'), 
      limit(pageSize)
    );
  } else {
    q = query(
      clientesRef, 
      where('borrado', '==', false),
      orderBy('nombre'), 
      limit(pageSize)
    );
  }
  
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }
  
  const snapshot = await getDocs(q);
  const clientes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
  return {
    clientes,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.docs.length === pageSize
  };
};

export const fetchTiendasPaginadas = async (currentService, pageSize = 50, lastDoc = null, filtros = {}) => {
  const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
  let q;
  
  // Construir la consulta base
  if (filtros.cliente) {
    q = query(
      tiendasRef, 
      where('cliente', '==', filtros.cliente),
      where('borrado', '==', false),
      orderBy('numTienda'), 
      limit(pageSize)
    );
  } else {
    q = query(
      tiendasRef, 
      where('borrado', '==', false),
      orderBy('numTienda'), 
      limit(pageSize)
    );
  }
  
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }
  
  const snapshot = await getDocs(q);
  let tiendas = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
  // Filtrar por término de búsqueda si existe (incluye localizacion)
  if (filtros.searchTerm && filtros.searchTerm.length >= 2) {
    const searchLower = filtros.searchTerm.toLowerCase();
    tiendas = tiendas.filter(tienda => 
      (tienda.numTienda && tienda.numTienda.toString().toLowerCase().includes(searchLower)) ||
      (tienda.nombreTienda && tienda.nombreTienda.toLowerCase().includes(searchLower)) ||
      (tienda.localizacion && tienda.localizacion.toLowerCase().includes(searchLower))
    );
  }
  
  return {
    tiendas,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: snapshot.docs.length === pageSize
  };
};

// Función para buscar clientes por nombre (búsqueda optimizada)
export const buscarClientes = async (currentService, searchTerm, limitCount = 20) => {
  if (!searchTerm || searchTerm.length < 2) return [];

  const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
  const q = query(
    clientesRef,
    where('borrado', '==', false),
    orderBy('nombre'),
    limit(limitCount)
  );
  
  const snapshot = await getDocs(q);
  const clientes = snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() }))
    .filter(cliente => 
      cliente.nombre && cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );
  
  return clientes;
};

// Función para buscar tiendas por número, nombre o localización (búsqueda optimizada)
export const buscarTiendas = async (currentService, searchTerm, limitCount = 20) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
  const q = query(
    tiendasRef,
    where('borrado', '==', false),
    orderBy('numTienda'),
    limit(limitCount)
  );
  
  const snapshot = await getDocs(q);
  const searchLower = searchTerm.toLowerCase();
  const tiendas = snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() }))
    .filter(tienda => 
      (tienda.numTienda && tienda.numTienda.toString().toLowerCase().includes(searchLower)) ||
      (tienda.nombreTienda && tienda.nombreTienda.toLowerCase().includes(searchLower)) ||
      (tienda.localizacion && tienda.localizacion.toLowerCase().includes(searchLower))
    );
  
  return tiendas;
};

// Función para obtener estadísticas rápidas
export const getEstadisticas = async (currentService) => {
  try {
    const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
    const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
    
    const [clientesSnapshot, tiendasSnapshot] = await Promise.all([
      getDocs(query(clientesRef, where('borrado', '==', false))),
      getDocs(query(tiendasRef, where('borrado', '==', false)))
    ]);
    
    return {
      totalClientes: clientesSnapshot.size,
      totalTiendas: tiendasSnapshot.size
    };
  } catch (error) {
    console.error('Error getting statistics:', error);
    return { totalClientes: 0, totalTiendas: 0 };
  }
};

// Exportar funciones de caché para uso en otros componentes
export { clearCache, getFromCache, setCache };

// Función para limpiar caché cuando se actualiza un usuario
export const clearUserCache = (serviceId, userId = null) => {
  if (userId) {
    // Limpiar caché específico del usuario
    const userCacheKey = generateCacheKey(serviceId, 'user_data', { userId });
    cache.delete(userCacheKey);
    console.log(`User cache cleared for: ${userId}`);
  } else {
    // Limpiar todo el caché del servicio
    clearCache(serviceId);
  }
};

// Función para limpiar caché cuando se actualiza un admin
export const clearAdminCache = (serviceId) => {
  // Limpiar caché de admins (paginados y colección completa)
  for (const [key] of cache) {
    if (
      (key.includes('admins_paginados') && key.startsWith(`${serviceId}_`)) ||
      key === `admins_completo_${serviceId}`
    ) {
      cache.delete(key);
    }
  }
  console.log(`Admin cache cleared for service: ${serviceId}`);
};

// Función optimizada para cargar solo clientes y tiendas necesarios para EditAdmin
export const fetchClientesYTiendasOptimizado = async (currentService, adminData = null) => {
  try {
    // Generar clave de caché
    const cacheKey = generateCacheKey(currentService.id, 'clientes_tiendas_optimizado', {
      adminId: adminData?.id || null,
      clientesAsignados: adminData?.clientes?.length || 0
    });

    // Intentar obtener del caché primero
    const cachedData = getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    let clientesData = [];
    let tiendasData = [];

    if (adminData && adminData.clientes && adminData.clientes.length > 0) {
      // Si el admin ya tiene clientes asignados, cargar solo esos y sus tiendas
      const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
      const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');

      // Cargar solo los clientes asignados
      const clientesPromises = adminData.clientes.map(clienteId => 
        getDoc(doc(clientesRef, clienteId))
      );
      const clientesDocs = await Promise.all(clientesPromises);
      
      clientesData = clientesDocs
        .filter(doc => doc.exists() && !doc.data().borrado)
        .map(doc => ({ id: doc.id, ...doc.data() }));

      // Cargar solo las tiendas de los clientes asignados
      const tiendasPromises = adminData.clientes.map(clienteId => 
        getDocs(query(tiendasRef, where('cliente', '==', clienteId), where('borrado', '==', false)))
      );
      const tiendasSnapshots = await Promise.all(tiendasPromises);
      
      tiendasData = tiendasSnapshots.flatMap(snapshot => 
        snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      );
    } else {
      // Si no hay clientes asignados, cargar solo la primera página
      const [clientesResult, tiendasResult] = await Promise.all([
        fetchClientesPaginados(currentService, 50),
        fetchTiendasPaginadas(currentService, 50)
      ]);
      
      clientesData = clientesResult.clientes;
      tiendasData = tiendasResult.tiendas;
    }

    const result = [clientesData, tiendasData];
    
    // Guardar en caché
    setCache(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error('Error fetching clientes y tiendas optimizado:', error);
    throw error;
  }
};

// Función para cargar más clientes cuando sea necesario
export const fetchMoreClientes = async (currentService, lastDoc, pageSize = 50) => {
  try {
    const result = await fetchClientesPaginados(currentService, pageSize, lastDoc);
    return result;
  } catch (error) {
    console.error('Error fetching more clientes:', error);
    throw error;
  }
};

// Función para cargar más tiendas cuando sea necesario
export const fetchMoreTiendas = async (currentService, lastDoc, pageSize = 50) => {
  try {
    const result = await fetchTiendasPaginadas(currentService, pageSize, lastDoc);
    return result;
  } catch (error) {
    console.error('Error fetching more tiendas:', error);
    throw error;
  }
};

// Función para buscar clientes específicos por nombre
export const buscarClientesEspecificos = async (currentService, searchTerm, limit = 20) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  try {
    const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
    const q = query(
      clientesRef,
      where('borrado', '==', false),
      orderBy('nombre'),
      limit(limit)
    );
    
    const snapshot = await getDocs(q);
    const clientes = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(cliente => 
        cliente.nombre && cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase())
      );
    
    return clientes;
  } catch (error) {
    console.error('Error searching clientes:', error);
    return [];
  }
};

// Función para buscar tiendas específicas por número o nombre
export const buscarTiendasEspecificas = async (currentService, searchTerm, limit = 20) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  try {
    const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
    const q = query(
      tiendasRef,
      where('borrado', '==', false),
      orderBy('numTienda'),
      limit(limit)
    );
    
    const snapshot = await getDocs(q);
    const tiendas = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(tienda => 
        (tienda.numTienda && tienda.numTienda.toString().includes(searchTerm)) ||
        (tienda.nombreTienda && tienda.nombreTienda.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    
    return tiendas;
  } catch (error) {
    console.error('Error searching tiendas:', error);
    return [];
  }
};

// Función para cargar clientes de forma lazy y por demanda
export const fetchClientesLazy = async (currentService, adminData = null, pageSize = 20) => {
  try {
    let clientesData = [];
    
    if (adminData && adminData.clientes && adminData.clientes.length > 0) {
      // Si tiene muchos clientes asignados, cargar solo los primeros
      const clientesToLoad = adminData.clientes.slice(0, pageSize);
      
      const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
      const clientesPromises = clientesToLoad.map(clienteId => 
        getDoc(doc(clientesRef, clienteId))
      );
      const clientesDocs = await Promise.all(clientesPromises);
      
      clientesData = clientesDocs
        .filter(doc => doc.exists() && !doc.data().borrado)
        .map(doc => ({ id: doc.id, ...doc.data() }));
    } else {
      // Si no hay clientes asignados, cargar solo la primera página
      const result = await fetchClientesPaginados(currentService, pageSize);
      clientesData = result.clientes;
    }

    return {
      clientes: clientesData,
      hasMore: adminData?.clientes?.length > pageSize || false,
      totalCount: adminData?.clientes?.length || 0,
      loadedCount: clientesData.length
    };
  } catch (error) {
    console.error('Error fetching clientes lazy:', error);
    throw error;
  }
};

// Función para cargar tiendas de forma lazy y por demanda
export const fetchTiendasLazy = async (currentService, clientesIds = [], pageSize = 20) => {
  try {
    let tiendasData = [];
    
    if (clientesIds.length > 0) {
      // Si hay clientes específicos, cargar solo las tiendas de esos clientes
      const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
      
      // Cargar tiendas de los clientes especificados, limitando por página
      const tiendasPromises = clientesIds.slice(0, 5).map(clienteId => // Limitar a 5 clientes por vez
        getDocs(query(tiendasRef, where('cliente', '==', clienteId), where('borrado', '==', false), limit(pageSize)))
      );
      const tiendasSnapshots = await Promise.all(tiendasPromises);
      
      tiendasData = tiendasSnapshots.flatMap(snapshot => 
        snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      ).slice(0, pageSize); // Limitar el total
    } else {
      // Si no hay clientes específicos, cargar solo la primera página
      const result = await fetchTiendasPaginadas(currentService, pageSize);
      tiendasData = result.tiendas;
    }

    return {
      tiendas: tiendasData,
      hasMore: tiendasData.length === pageSize,
      totalCount: 0, // No calculamos el total para evitar consultas costosas
      loadedCount: tiendasData.length
    };
  } catch (error) {
    console.error('Error fetching tiendas lazy:', error);
    throw error;
  }
};

// Función para cargar más clientes específicos (para usuarios con muchos clientes)
export const fetchMoreClientesEspecificos = async (currentService, clientesIds, loadedCount, pageSize = 20) => {
  try {
    const startIndex = loadedCount;
    const endIndex = startIndex + pageSize;
    const clientesToLoad = clientesIds.slice(startIndex, endIndex);
    
    if (clientesToLoad.length === 0) {
      return {
        clientes: [],
        hasMore: false,
        loadedCount: loadedCount
      };
    }

    const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
    const clientesPromises = clientesToLoad.map(clienteId => 
      getDoc(doc(clientesRef, clienteId))
    );
    const clientesDocs = await Promise.all(clientesPromises);
    
    const nuevosClientes = clientesDocs
      .filter(doc => doc.exists() && !doc.data().borrado)
      .map(doc => ({ id: doc.id, ...doc.data() }));

    return {
      clientes: nuevosClientes,
      hasMore: endIndex < clientesIds.length,
      loadedCount: loadedCount + nuevosClientes.length
    };
  } catch (error) {
    console.error('Error fetching more clientes específicos:', error);
    throw error;
  }
};

// Función para cargar más tiendas específicas
export const fetchMoreTiendasEspecificas = async (currentService, clientesIds, loadedCount, pageSize = 20) => {
  try {
    const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
    
    // Calcular qué clientes necesitamos cargar
    // Si loadedCount es 0, empezamos desde el primer cliente
    // Si loadedCount > 0, calculamos cuántos clientes ya procesamos
    const clientesPorCarga = 5; // Cargar tiendas de 5 clientes por vez
    
    // Calcular cuántos clientes ya hemos procesado para servicios
    // Si loadedCount es 0, empezamos desde 0
    // Si loadedCount > 0, calculamos basándonos en cuántos servicios ya cargamos
    let startClienteIndex = 0;
    
    if (loadedCount > 0) {
      // Si ya cargamos servicios, calculamos cuántos clientes ya procesamos
      // Asumimos que cada cliente puede tener hasta pageSize servicios
      startClienteIndex = Math.floor(loadedCount / (pageSize * 0.8)); // Factor de 0.8 para ser conservador
    }
    
    // Asegurar que no excedamos el número total de clientes
    if (startClienteIndex >= clientesIds.length) {
      return {
        tiendas: [],
        hasMore: false,
        loadedCount: loadedCount
      };
    }
    
    const clientesToLoad = clientesIds.slice(startClienteIndex, startClienteIndex + clientesPorCarga);
    
    if (clientesToLoad.length === 0) {
      return {
        tiendas: [],
        hasMore: false,
        loadedCount: loadedCount
      };
    }

    console.log(`Loading services for clients: ${clientesToLoad.join(', ')} (startIndex: ${startClienteIndex})`);

    const tiendasPromises = clientesToLoad.map(clienteId => 
      getDocs(query(tiendasRef, where('cliente', '==', clienteId), where('borrado', '==', false), limit(pageSize)))
    );
    const tiendasSnapshots = await Promise.all(tiendasPromises);
    
    const nuevasTiendas = tiendasSnapshots.flatMap(snapshot => 
      snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
    );

    console.log(`Loaded ${nuevasTiendas.length} new services from ${clientesToLoad.length} clients`);

    // Verificar si hay más clientes para procesar
    const hasMore = startClienteIndex + clientesPorCarga < clientesIds.length;

    return {
      tiendas: nuevasTiendas,
      hasMore: hasMore,
      loadedCount: loadedCount + nuevasTiendas.length
    };
  } catch (error) {
    console.error('Error fetching more tiendas específicas:', error);
    throw error;
  }
};

// Función optimizada para cargar solo datos esenciales inicialmente
export const fetchClientesYTiendasEsenciales = async (currentService, adminData = null) => {
  try {
    // Generar clave de caché
    const cacheKey = generateCacheKey(currentService.id, 'clientes_tiendas_esenciales', {
      adminId: adminData?.id || null,
      clientesAsignados: adminData?.clientes?.length || 0
    });

    // Intentar obtener del caché primero
    const cachedData = getFromCache(cacheKey);
    if (cachedData) {
      return cachedData;
    }

    let clientesData = [];
    let tiendasData = [];
    let hasMoreClientes = false;
    let hasMoreTiendas = false;

    if (adminData && adminData.clientes && adminData.clientes.length > 0) {
      // Si tiene clientes asignados, cargar solo los primeros 10
      const clientesToLoad = adminData.clientes.slice(0, 10);
      
      const clientesRef = collection(db, 'servicios', currentService.id, 'clientes');
      const clientesPromises = clientesToLoad.map(clienteId => 
        getDoc(doc(clientesRef, clienteId))
      );
      const clientesDocs = await Promise.all(clientesPromises);
      
      clientesData = clientesDocs
        .filter(doc => doc.exists() && !doc.data().borrado)
        .map(doc => ({ id: doc.id, ...doc.data() }));

      // Cargar solo las tiendas de los primeros 5 clientes
      const clientesParaTiendas = adminData.clientes.slice(0, 5);
      const tiendasRef = collection(db, 'servicios', currentService.id, 'tiendas');
      const tiendasPromises = clientesParaTiendas.map(clienteId => 
        getDocs(query(tiendasRef, where('cliente', '==', clienteId), where('borrado', '==', false), limit(10)))
      );
      const tiendasSnapshots = await Promise.all(tiendasPromises);
      
      tiendasData = tiendasSnapshots.flatMap(snapshot => 
        snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      );

      hasMoreClientes = adminData.clientes.length > 10;
      hasMoreTiendas = adminData.clientes.length > 5 || tiendasData.length >= 50;
    } else {
      // Si no hay clientes asignados, cargar solo la primera página
      const [clientesResult, tiendasResult] = await Promise.all([
        fetchClientesPaginados(currentService, 20),
        fetchTiendasPaginadas(currentService, 20)
      ]);
      
      clientesData = clientesResult.clientes;
      tiendasData = tiendasResult.tiendas;
      hasMoreClientes = clientesResult.hasMore;
      hasMoreTiendas = tiendasResult.hasMore;
    }

    const result = {
      clientes: clientesData,
      tiendas: tiendasData,
      hasMoreClientes,
      hasMoreTiendas,
      totalClientes: adminData?.clientes?.length || 0,
      totalTiendas: 0 // No calculamos el total para evitar consultas costosas
    };
    
    // Guardar en caché
    setCache(cacheKey, result);
    
    return result;
  } catch (error) {
    console.error('Error fetching clientes y tiendas esenciales:', error);
    throw error;
  }
};